import React, { useState } from 'react';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import { bool, func, node, shape } from 'prop-types';
import * as R from 'ramda';
import { useUserRolePermissions } from 'hooks';
import { AUTH_TYPE, CUSTOMER_AUTH_TYPE_OPTIONS } from 'feature/loading-management/utils/const';
import { useTheme } from '@mui/material/styles';
import CustomerInfoForm from './CustomerInfoForm';
import { CUSTOMER_INFO_FIELD_KEYS, maxLengthError, requiredError, volumeError } from './validators';

const getAuthorizationTypeLabel = authorizationType =>
  R.find(R.propEq('value', authorizationType), AUTH_TYPE)?.label || authorizationType || '--';

const getControlTypeLabel = R.cond([
  [R.equals('SINGLE'), R.always('Single Terminal')],
  [R.equals('GLOBAL'), R.always('Multiple-Terminal Shared')],
  [R.equals('ALL'), R.always('Multiple-Terminal')],
  [R.T, R.always('--')],
]);

const NO_VOLUME_TYPES = ['UNLIMITED', 'SPECIFIC'];
const BOTH_VOLUME_TYPES = ['MONTHLY', 'WEEKLY', 'DAILY'];

const CustomerInfoSection = ({
  children,
  customerAuth,
  originalCustomerAuth,
  setCustomerAuth,
  isCustomerInfoSectionEnabled,
  isEditMode,
  setValidation,
  validation,
}) => {
  const { isProductLoadingManage, isProductLoadingView } = useUserRolePermissions();
  const [isExpanded, setIsExpanded] = useState(false);
  const theme = useTheme();
  const { authorizationType, controlType, customerIdName, customerName } = customerAuth;

  const canEdit = isProductLoadingManage && isEditMode;
  const canView = isProductLoadingManage || isProductLoadingView || !isEditMode;
  const isReadOnly = !canEdit || !isEditMode;

  React.useEffect(() => {
    if (!isCustomerInfoSectionEnabled) {
      return;
    }

    const isValidType = CUSTOMER_AUTH_TYPE_OPTIONS.some(({ value }) => value === authorizationType);
    if (authorizationType && !isValidType) {
      setCustomerAuth(R.assoc('authorizationType', undefined));
    }
  }, [authorizationType, isCustomerInfoSectionEnabled, setCustomerAuth]);

  React.useEffect(() => {
    if (!isCustomerInfoSectionEnabled) {
      return;
    }

    const isValidType = CUSTOMER_AUTH_TYPE_OPTIONS.some(({ value }) => value === authorizationType);
    const showAuthVolume = authorizationType && !NO_VOLUME_TYPES.includes(authorizationType);
    const showRenewalVolume = BOTH_VOLUME_TYPES.includes(authorizationType);
    setValidation(current => ({
      ...current,
      address: maxLengthError(customerAuth.address, 50),
      address2: maxLengthError(customerAuth.address2, 50),
      authorizationType: isValidType ? '' : 'Required',
      authorizationVolume:
        showAuthVolume && authorizationType !== 'ONELOAD'
          ? volumeError(customerAuth.authorizationVolume)
          : '',
      customerName: requiredError(customerName) || maxLengthError(customerName, 40),
      phoneNumber: maxLengthError(customerAuth.phoneNumber, 20),
      renewalVolume: showRenewalVolume ? volumeError(customerAuth.renewalVolume) : '',
    }));
  }, [authorizationType, customerAuth, customerName, isCustomerInfoSectionEnabled, setValidation]);

  const hasChanges = !R.equals(
    R.pick(CUSTOMER_INFO_FIELD_KEYS, originalCustomerAuth),
    R.pick(CUSTOMER_INFO_FIELD_KEYS, customerAuth)
  );

  const customerInfoValidation = R.omit(['controlNumbers'], validation);
  const hasValidationErrors = Object.values(customerInfoValidation).some(value =>
    R.not(R.isEmpty(value))
  );

  return (
    <Box
      sx={{
        mb: 1.5,
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: theme.palette.surface.medium,
          border: '1px solid',
          borderColor: 'grey.300',
          borderRadius: 1,
          display: 'flex',
          justifyContent: 'space-between',
          mb: 1.5,
          padding: '10px 12px',
        }}
      >
        <Stack spacing={1}>
          <Typography fontWeight={700} variant="body1">
            <Typography component="span" sx={{ color: 'text.secondary', mr: 1 }} variant="caption">
              {customerIdName}
            </Typography>
            {customerName}
          </Typography>
          <Stack alignItems="center" direction="row" spacing={1}>
            <Chip
              label={getAuthorizationTypeLabel(authorizationType)}
              size="small"
              sx={{ backgroundColor: 'white' }}
            />
            <Chip
              label={getControlTypeLabel(controlType)}
              size="small"
              sx={{ backgroundColor: 'white' }}
            />
            {hasChanges && (
              <Tooltip title="Unsaved changes">
                <Box
                  sx={{ backgroundColor: 'warning.main', borderRadius: '50%', height: 8, width: 8 }}
                />
              </Tooltip>
            )}
            {hasValidationErrors && (
              <ErrorRoundedIcon fontSize="small" sx={{ color: 'error.main' }} />
            )}
          </Stack>
        </Stack>
        {canEdit ? (
          <IconButton
            aria-label={isExpanded ? 'collapseCustomerInfo' : 'expandCustomerInfo'}
            onClick={() => setIsExpanded(current => !current)}
            sx={{ alignItems: 'center', borderRadius: 1, display: 'flex' }}
          >
            <Stack alignItems="center" direction="row" spacing={0.5}>
              <EditRoundedIcon sx={{ color: 'primary.light', fontSize: 20 }} />
            </Stack>
          </IconButton>
        ) : (
          canView && (
            <IconButton
              aria-label="viewCustomerInfo"
              onClick={() => setIsExpanded(current => !current)}
            >
              <VisibilityRoundedIcon sx={{ color: 'primary.light' }} />
            </IconButton>
          )
        )}
      </Box>
      {isExpanded &&
        (isCustomerInfoSectionEnabled ? (
          <CustomerInfoForm
            customerAuth={customerAuth}
            isReadOnly={isReadOnly}
            setCustomerAuth={setCustomerAuth}
            setValidation={setValidation}
            validation={validation}
          />
        ) : (
          children
        ))}
    </Box>
  );
};

CustomerInfoSection.propTypes = {
  children: node,
  customerAuth: shape({}),
  isCustomerInfoSectionEnabled: bool,
  isEditMode: bool.isRequired,
  originalCustomerAuth: shape({}),
  setCustomerAuth: func.isRequired,
  setValidation: func.isRequired,
  validation: shape({}).isRequired,
};

CustomerInfoSection.defaultProps = {
  children: null,
  customerAuth: {},
  isCustomerInfoSectionEnabled: false,
  originalCustomerAuth: {},
};

export default CustomerInfoSection;
